'use client'

import { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react'

import { getGeneralInfo, setCurrency as setCurrencyInit, setLocale as setLanguage } from '@/lib/utils'
import { generalInfo } from '@/types/basics.type'
import { eGeneralInfo, languages } from '@/types/enum'

type ConfigType = {
  basics: generalInfo[]
  locale: languages
  currency: 'VND' | 'USD'
  getBasic: (key: string) => string | undefined
  setLocale: Dispatch<SetStateAction<languages>>
  setCurrency: Dispatch<SetStateAction<'VND' | 'USD'>>
  openChat: boolean
  setOpenChat: Dispatch<SetStateAction<boolean>>
}

const ConfigContext = createContext<ConfigType | undefined>(undefined)

export const useConfigContext = () => {
  const context = useContext(ConfigContext)
  if (!context) {
    throw new Error('useConfigContext must be used within a ConfigProvider')
  }
  return context
}

export default function ConfigProvider({
  children,
  basics,
  lang,
  currency: initialCurrency,
}: {
  children: React.ReactNode
  basics: generalInfo[]
  lang: languages
  currency: 'VND' | 'USD'
}) {
  const [locale, setLocale] = useState(lang)
  const [currency, setCurrency] = useState<'VND' | 'USD'>(initialCurrency)
  const [openChat, setOpenChat] = useState(false)
  const getBasic = (key: string) => getGeneralInfo(basics, key as eGeneralInfo, locale)

  useEffect(() => {
    setLocale(lang)
    setLanguage(lang)
  }, [lang])

  useEffect(() => {
    setCurrency(initialCurrency)
    setCurrencyInit(initialCurrency)
  }, [initialCurrency])

  return <ConfigContext.Provider value={{ basics, locale, currency, getBasic, setLocale, setCurrency, openChat, setOpenChat }}>{children}</ConfigContext.Provider>
}
